import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/buttons/button";
import { InfoCard } from "../../components/cards/infoCard";
import { EmptyEventState } from "../../components/empty/emptyEventState";
import { FullEventCard } from "../../components/event/fullEventCard";
import { Icon } from "../../components/icon/icon";
import { EventCardsLoader } from "../../components/loaders/loaders";
import { WelcomeModal } from "../../components/modal/app_modals/welcomeModal";
import { SentinelDiv } from "../../components/sentinelDiv";
import { PageHeader } from "../../components/typography/pageHeader";
import { useModalControl } from "../../customeHooks/useModalControl";
import { useWebsiteButton } from "../../customeHooks/useWebsiteButtons";
import { useGetEventsQuery } from "../../redux/features/event/event.api";
import { CreatedEvent } from "../../redux/features/event/event.types";
import { useAppSelector } from "../../redux/hooks";
import { QueryParams } from "../../utils/types";

export const HomePage = () => {
  const lastPage = useRef(0);
  const [eventsList, setEventsList] = useState<CreatedEvent[]>([]);
  const [eventsQuery, setEventsQuery] = useState<QueryParams>({
    page: 1,
    limit: 10,
  });

  const { profile, hasCreatedEvent } = useAppSelector((state) => state.auth);

  const navigate = useNavigate();

  const { goToFindEvents } = useWebsiteButton();

  const {
    currentData: events,
    isFetching,
    isUninitialized,
    refetch: refetchEvents,
  } = useGetEventsQuery(
    {
      status: "upcoming",
      page: eventsQuery.page,
      limit: eventsQuery.limit,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  useEffect(() => {
    if (profile?.isFirstLogin) {
      handleModalOpen("welcomeModal");
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (
      !!events?.data?.data &&
      eventsQuery.page! > lastPage.current &&
      eventsQuery.page! > 1 &&
      !isFetching
    ) {
      setEventsList((prev) => [...prev, ...events.data.data]);
      lastPage.current = eventsQuery.page!;
    } else if (!!events?.data?.data && eventsQuery.page! === 1 && !isFetching) {
      setEventsList((prev) => [...events.data.data]);
      lastPage.current = eventsQuery.page!;
    }
    // eslint-disable-next-line
  }, [events, isFetching]);

  return (
    <div className="w-full">
      <div className="flex w-full items-center justify-between gap-4">
        <PageHeader
          header={
            <div className="flex gap-1.5">
              <span>Hello, {profile?.firstName} 😄</span>
            </div>
          }
        />

        <Button
          text="Create Event"
          icon="plus"
          type="button"
          className="hidden max-w-fit md:flex"
          onClick={() => {
            if (hasCreatedEvent) {
              navigate("/event/new", { replace: true });
            } else {
              navigate("/complete-profile");
            }
          }}
        />
      </div>

      <div className="mt-8 flex flex-col gap-1">
        <InfoCard
          title="Find interesting events around you 🔎"
          text="Click here to find events around you"
          className="max-w-full !bg-light-blue"
          rightIcon="arrow-right"
          onClick={() => {
            goToFindEvents();
          }}
        />

        <div className="mt-2 flex w-full items-center gap-2 rounded-2xl bg-white p-3 text-xs font-bold text-grey-500">
          <Icon svg="clock" width={16} height={26} />
          <p>UPCOMING EVENTS</p>
        </div>

        {!isFetching && [undefined, 0].includes(events?.data?.totalCount) ? (
          <EmptyEventState eventStatus="upcoming" />
        ) : undefined}

        {eventsList.length > 0 ? (
          <div className="flex flex-col gap-3">
            {eventsList.map((event, idx) => {
              return (
                <FullEventCard
                  event={event}
                  key={`${event._id}-${idx}`}
                  refetchEvents={() => {
                    refetchEvents();
                  }}
                />
              );
            })}
          </div>
        ) : undefined}

        {isFetching ? <EventCardsLoader numberOfCards={4} /> : undefined}
      </div>

      <SentinelDiv
        onVisible={() => {
          if (
            eventsQuery.page! <
              Math.ceil((events?.data?.totalCount ?? 0) / eventsQuery.limit!) &&
            !isFetching &&
            !isUninitialized
          ) {
            setEventsQuery({
              ...eventsQuery,
              page: eventsQuery.page! + 1,
            });
          }
        }}
        loading={false}
        className="w-full text-center text-grey-300 opacity-0"
      />

      <WelcomeModal
        open={modalOpen && modal === "welcomeModal"}
        handleClose={() => {
          handleModalClose();
        }}
      />
    </div>
  );
};
